import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { sendFriendInvite } from '../../services/email/emailService';

interface InviteFriendModalProps {
  onClose: () => void;
}

const InviteFriendModal: React.FC<InviteFriendModalProps> = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { currentUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      if (!currentUser.email) {
        setError('User email not found');
        return;
      }

      const signupUrl = `${window.location.origin}/signup?ref=${currentUser.email}`;
      const success = await sendFriendInvite({
        inviterName: `${currentUser.displayName || currentUser.email}`,
        inviterEmail: currentUser.email,
        inviteeEmail: email,
        signupUrl
      });

      if (success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError('Failed to send invitation. Please try again.');
      }
    } catch (err) {
      setError('Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto p-4">
      <DialogPanel className="bg-white rounded-lg p-6 w-full max-w-md mx-auto min-w-0">
        <div className="flex justify-between items-center mb-4">
          <DialogTitle className="text-xl font-semibold">Invite Friend</DialogTitle>
          <button
            onClick={onClose} aria-label="Close dialog"
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className="text-green-600 text-center py-4">
            Invitation sent successfully!
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Friend's Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your friend's email"
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm mb-4">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              <Send size={20} />
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </form>
        )}
      </DialogPanel>
      </div>
    </Dialog>
  );
};

export default InviteFriendModal;
